import { DeviceSyncField, type DeviceSyncResponse } from '@am3nd-org/pointcraft-types';

import {
  fetchDeviceSyncPayload,
  type BusinessDeviceSyncFields,
} from 'src/utils/Services/device-sync-v4.service';

import useUserDataStore from 'src/store/user';
import { mapBusinessData, mapBusinessUser } from 'src/auth/business-user-profile';
import { useDeviceSyncStore, type DeviceSyncVersions } from 'src/store/DeviceSync';
import { getSessionGeneration, isSessionGenerationCurrent } from 'src/auth/session-lifecycle';

import { PointPayIdentityError, type PointPayIdentity } from './session-identity';

export const POINTPAY_SYNC_FIELDS = [
  DeviceSyncField.BUSINESS_DATA,
  DeviceSyncField.USER_DATA,
  DeviceSyncField.BUSINESS_DEVICES,
  DeviceSyncField.PROVIDER_DETAILS,
  DeviceSyncField.RECURRING_PLANS,
];
const VERSION_KEYS = [
  'businessProfile',
  'userProfile',
  'businessDevices',
  'providerDetails',
  'recurringPlans',
] as const;

export function validatePointPayUserData(
  identity: PointPayIdentity,
  payload: Pick<DeviceSyncResponse, 'businessData' | 'userData'>
) {
  const business = payload.businessData;
  const users = payload.userData;
  const user = users?.find((candidate) => candidate.id === identity.userId);
  if (
    !business ||
    business.id !== identity.businessId ||
    !user?.isActive ||
    users?.some((candidate) => candidate.businessId !== identity.businessId)
  ) {
    throw new PointPayIdentityError(
      'Your user no longer has access to this business. Please sign in again.'
    );
  }
  return { business, users: users!, user };
}

export function publishPointPayUser(
  identity: PointPayIdentity,
  payload: Pick<DeviceSyncResponse, 'businessData' | 'userData'>
) {
  const { business, users, user } = validatePointPayUserData(identity, payload);
  const currentUser = mapBusinessUser(user, business);
  useUserDataStore.setState((state) => ({
    business: mapBusinessData(business),
    profile: {
      currentUser,
      allUsers: users.map((item) => mapBusinessUser(item, business)),
      email: currentUser.email,
    },
    system: { ...state.system, activeFeatures: [...business.configurations.features] },
  }));
}

// Deduplicate only within this identity and logout generation.
let inFlight: { key: string; promise: Promise<void> } | null = null;

export function refreshPointPayUserData(identity: PointPayIdentity): Promise<void> {
  const generation = getSessionGeneration();
  const key = `${generation}:${identity.businessId}:${identity.uid}`;
  if (inFlight?.key === key) return inFlight.promise;
  const promise = (async () => {
    const payload = await fetchDeviceSyncPayload(POINTPAY_SYNC_FIELDS);
    if (!isSessionGenerationCurrent(generation)) return;
    validatePointPayUserData(identity, payload);
    useDeviceSyncStore.getState().resetIfTenantChanged(identity.businessId);
    // Explicitly select safe slices even if a server returns extra fields.
    useDeviceSyncStore.getState().applySlices({
      businessData: payload.businessData,
      userData: payload.userData,
      businessDevices: payload.businessDevices,
      providerDetails: payload.providerDetails,
      recurringPlans: payload.recurringPlans,
    });
    publishPointPayUser(identity, payload);
  })().finally(() => {
    if (inFlight?.promise === promise) inFlight = null;
  });
  inFlight = { key, promise };
  return promise;
}

let businessChanges: { key: string; promise: Promise<void> } | null = null;

export function refreshPointPayBusinessChanges(
  identity: PointPayIdentity,
  fields: BusinessDeviceSyncFields
): Promise<void> {
  const versions: DeviceSyncVersions = {};
  VERSION_KEYS.forEach((key) => {
    const value = fields[key] as { version?: unknown } | undefined;
    if (typeof value?.version === 'number') versions[key] = value.version;
  });
  const generation = getSessionGeneration();
  const key = `${generation}:${identity.businessId}:${identity.uid}`;
  const previous = businessChanges?.key === key ? businessChanges.promise : Promise.resolve();
  // A newer notification must fetch after the previous request settles; sharing
  // its response could acknowledge a user/role change that it never loaded.
  const promise = previous
    .catch(() => undefined)
    .then(async () => {
      if (!isSessionGenerationCurrent(generation)) return;
      if (
        !VERSION_KEYS.some(
          (versionKey) =>
            versions[versionKey] !== undefined &&
            versions[versionKey] !== useDeviceSyncStore.getState().versions[versionKey]
        )
      )
        return;
      await refreshPointPayUserData(identity);
      if (isSessionGenerationCurrent(generation))
        useDeviceSyncStore.getState().setVersions(versions);
    })
    .finally(() => {
      if (businessChanges?.promise === promise) businessChanges = null;
    });
  businessChanges = { key, promise };
  return promise;
}
